import { useEffect, useState } from "react";
import { Package2, Pencil, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { act, useFetch } from "@/lib/hooks";
import { da } from "@/lib/format";
import { Empty, Field, Loading, Panel } from "@/components/common";
import { selectCls } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const packageTotal = (p: any) => p.lines.reduce((s: number, l: any) => s + l.qty * (l.item?.unit_price || 0), 0);

function PackageDialog({ pkg, items, open, onOpenChange, onSaved }: any) {
  const [name, setName] = useState("");
  const [lines, setLines] = useState<any[]>([]);
  const [active, setActive] = useState(true);
  useEffect(() => {
    if (open) {
      setName(pkg?.name || "");
      setLines(pkg ? pkg.lines.map((l: any) => ({ item_id: l.item_id, qty: l.qty })) : []);
      setActive(pkg ? pkg.active : true);
    }
  }, [open, pkg]);
  const save = async () => {
    const body = { name, lines, active };
    if (await act(() => (pkg ? api.put(`/packages/${pkg.id}`, body) : api.post("/packages", body)), "Forfait enregistré")) {
      onSaved();
      onOpenChange(false);
    }
  };
  const upd = (i: number, k: string, v: any) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="package-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{pkg ? "Modifier le forfait" : "Nouveau forfait"}</DialogTitle></DialogHeader>
        <Field label="Nom du forfait"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vidange complète" data-testid="package-name-input" /></Field>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_auto] gap-2" data-testid={`package-line-${i}`}>
              <select className={selectCls} value={l.item_id} onChange={(e) => upd(i, "item_id", e.target.value)} data-testid={`package-line-item-${i}`}>
                {items.map((it: any) => <option key={it.id} value={it.id}>{it.kind === "part" ? "[Pièce] " : ""}{it.label}{it.active ? "" : " (inactif)"}</option>)}
              </select>
              <Input type="number" min={0.01} step="any" value={l.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} data-testid={`package-line-qty-${i}`} />
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="px-2 text-muted-foreground hover:text-red-400" data-testid={`package-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button variant="outline" onClick={() => items.length && setLines([...lines, { item_id: items[0].id, qty: 1 }])} data-testid="package-add-line-btn"><Plus className="mr-1 h-4 w-4" />Ajouter un article</Button>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="package-active-checkbox" />Forfait actif</label>
        <p className="text-xs text-muted-foreground">Les prix viennent du catalogue au moment de l'insertion et restent modifiables ligne par ligne sur le devis.</p>
        <Button className="h-11" disabled={name.trim().length < 2 || !lines.length} onClick={save} data-testid="package-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PackagesPanel({ items, write }: { items: any[]; write: boolean }) {
  const { data, loading, reload } = useFetch<any[]>("/packages");
  const [edit, setEdit] = useState<any>(undefined);
  return (
    <Panel title="Forfaits" actions={write && <Button size="sm" onClick={() => setEdit(null)} data-testid="new-package-btn"><Plus className="mr-1 h-4 w-4" />Forfait</Button>} testId="packages-panel">
      {loading ? <Loading /> : !data?.length ? <Empty text="Aucun forfait. Créez-en un pour insérer plusieurs lignes d'un clic dans un devis." /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((p) => (
            <div key={p.id} className={`rounded-sm border border-border p-4 ${p.active ? "" : "opacity-50"}`} data-testid={`package-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold"><Package2 className="h-4 w-4 text-primary" />{p.name}{!p.active && <span className="rounded-sm border border-border px-1.5 text-[10px] uppercase text-muted-foreground">Inactif</span>}</div>
                {write && <button onClick={() => setEdit(p)} className="text-muted-foreground hover:text-foreground" data-testid={`package-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>}
              </div>
              <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                {p.lines.map((l: any, i: number) => <li key={i}>{l.qty} × {l.item.label}{l.item.active ? (l.item.unit_price ? "" : " (prix à définir)") : " (article inactif, ignoré)"}</li>)}
              </ul>
              <div className="mt-3 font-mono-data text-sm" data-testid={`package-total-${p.id}`}>Total HT catalogue : {da(packageTotal(p))}</div>
            </div>
          ))}
        </div>
      )}
      <PackageDialog pkg={edit} items={items.filter((i) => i.active || edit?.lines?.some((l: any) => l.item_id === i.id))} open={edit !== undefined} onOpenChange={(o: boolean) => !o && setEdit(undefined)} onSaved={reload} />
    </Panel>
  );
}
